import {Button} from 'platform/components';
import {HStack, Show} from 'platform/foundation';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {SourcingFilterPresetsControl} from '../../../hooks/useSourcingFilterPresets';
import {DEFAULT_FILTERS} from '../../../types/FiltersType';
import {hasSomeFilters} from '../../../utils/hasSomeFilters';
import {useFilters} from '../../FiltersContext/FiltersContext';

interface FilterTagButtonsProps extends TestIdProps {
  tagCount: number;
  collapsedTagCount: number;
  filterPresetsControl: SourcingFilterPresetsControl;
  isExpanded: boolean;
  showSaveButton?: boolean;
  toggleIsExpanded: () => void;
}

export function FilterTagButtons(props: FilterTagButtonsProps) {
  const {filters, onChange, onClearFilters} = useFilters();

  const handleResetFilterPreset = () => {
    const selectedFilterPreset = props.filterPresetsControl?.selectedFilterPreset;
    if (!selectedFilterPreset) {
      return;
    }

    onChange({...DEFAULT_FILTERS, ...selectedFilterPreset.filters});
  };

  const isFilterPresetSelected = isNotNil(props.filterPresetsControl?.selectedFilterPreset);
  const isFilterPresetOutdated = isFilterPresetSelected && props.filterPresetsControl.isOutdated;
  const shouldShowExpandButton = props.collapsedTagCount > 0;
  const shouldShowClearButton = props.tagCount > 0;
  const shouldShowResetButton = props.showSaveButton && isFilterPresetOutdated;
  const shouldShowUpdateButton =
    props.showSaveButton && isFilterPresetOutdated && hasSomeFilters(filters);
  const shouldShowCreateButton =
    props.showSaveButton && !isFilterPresetSelected && hasSomeFilters(filters);

  return (
    <HStack spacing={2}>
      <Show when={shouldShowExpandButton}>
        <Button
          variant="link"
          title={
            props.isExpanded
              ? i18n.t('general.actions.hide')
              : i18n.t('entity.sourcing.labels.nMore', {
                  count: props.collapsedTagCount,
                })
          }
          onClick={props.toggleIsExpanded}
          data-testid={suffixTestId('chipsExpandToggle', props)}
        />
      </Show>
      <Show when={shouldShowClearButton}>
        <Button
          title={i18n.t('entity.sourcing.labels.clearFilters')}
          variant="dangerLink"
          leftIcon="action/delete"
          onClick={onClearFilters}
          data-testid={suffixTestId('filterClear', props)}
        />
      </Show>
      <Show when={shouldShowResetButton}>
        <Button
          variant="link"
          leftIcon="action/cached"
          onClick={handleResetFilterPreset}
          title={i18n.t('general.actions.setToDefault')}
          data-testid={suffixTestId('setToDefault', props)}
        />
      </Show>
      <Show when={shouldShowCreateButton}>
        <Button
          variant="link"
          leftIcon="content/save"
          onClick={props.filterPresetsControl.onCreateFilterPreset}
          title={i18n.t('entity.sourcing.filterPresets.actions.createFilter')}
          data-testid={suffixTestId('createFilter', props)}
        />
      </Show>
      <Show when={shouldShowUpdateButton}>
        <Button
          variant="link"
          leftIcon="content/save"
          onClick={props.filterPresetsControl.onUpdateSelectedFilterPreset}
          title={i18n.t('entity.sourcing.filterPresets.actions.saveFilter')}
          data-testid={suffixTestId('saveFilter', props)}
        />
      </Show>
    </HStack>
  );
}
